type WorkerLike = {
  state: string;
  addEventListener: (type: 'statechange', listener: () => void) => void;
  removeEventListener: (type: 'statechange', listener: () => void) => void;
};

export type RegistrationLike = {
  active: WorkerLike | null;
  installing: WorkerLike | null;
  waiting: WorkerLike | null;
};

/**
 * `navigator.serviceWorker.ready` pode resolver outro worker. O getToken do FCM
 * no iOS exige que ESTE registration esteja activated — senão o PWA concede a
 * permissão e o app ainda falha.
 */
export function waitForServiceWorkerActive(
  registration: RegistrationLike,
  timeoutMs = 8000,
): Promise<boolean> {
  const incoming = registration.installing || registration.waiting;
  if (registration.active && !incoming) return Promise.resolve(true);

  const worker = incoming ?? registration.active;
  if (!worker) return Promise.resolve(false);
  if (worker.state === 'activated') return Promise.resolve(true);

  return new Promise((resolve) => {
    const finish = (ok: boolean) => {
      clearTimeout(timer);
      worker.removeEventListener('statechange', onChange);
      resolve(ok);
    };

    const onChange = () => {
      if (worker.state === 'activated') finish(true);
    };

    const timer = setTimeout(() => finish(worker.state === 'activated'), timeoutMs);
    worker.addEventListener('statechange', onChange);
  });
}

/** iOS só entrega PushManager depois que este SW controla a página. */
export function waitForServiceWorkerController(timeoutMs = 8000): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(false);
  }
  if (navigator.serviceWorker.controller) return Promise.resolve(true);

  return new Promise((resolve) => {
    const finish = (ok: boolean) => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener('controllerchange', onChange);
      resolve(ok);
    };
    const onChange = () => finish(true);
    const timer = setTimeout(
      () => finish(Boolean(navigator.serviceWorker.controller)),
      timeoutMs,
    );
    navigator.serviceWorker.addEventListener('controllerchange', onChange);
  });
}
